import React, { useCallback, useEffect, useState } from 'react';
import CheckoutLayout from '../CheckoutLayout';

import SafeEnvironment from '../SafeEnvironment';
import { Checkbox, Input, Select, Text } from '~/components';
import { useForm } from 'react-hook-form';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Keyboard, TouchableOpacity, View } from 'react-native';
import { isCpf, isFullName } from '~/utils/yup';
import useAppNavigation from '~/hooks/useAppNavigation';
import { getBanksUseCase } from '~/useCases/AssegurouAutoUseCases/GetBanksUseCase';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '~/navigation/types';
import { useCheckout, useSalesFunnel } from '~/stores';
import { CheckoutStepNames } from '~/utils/salesFunnel';

interface AccountDebitSchema extends Checkout.AccountDebit {
  acceptNews: boolean;
  acceptGeneralConditions: boolean;
}

const schema = yup
  .object()
  .shape({
    name: isFullName(),
    cpf: isCpf(),
    bank: yup.string().required('Informe o banco'),
    agency: yup.number().required('Informe a agência'),
    accountNumber: yup.number().required('Informe o número da conta'),
    accountDigit: yup.number().required(' '),
    acceptNews: yup.bool().isTrue().required(),
    acceptGeneralConditions: yup.bool().isTrue().required(),
  })
  .required();

const AccountDebit: React.FC = () => {
  const { setPage, setAccountDebit } = useCheckout();
  const { push } = useAppNavigation();
  const { newStepLog } = useSalesFunnel();

  const { params } =
    useRoute<RouteProp<RootStackParamList, 'MarisaServicos_Checkout'>>();

  const [banksOptions, setBanksOptions] = useState<Select.Option[]>([]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AccountDebitSchema>({
    resolver: yupResolver(schema),
  });

  const onSubmit = useCallback(
    async (data: AccountDebitSchema) => {
      Keyboard.dismiss();
      setAccountDebit(data);

      const nextStep = (params.step || 0) + 1;
      setPage(nextStep);

      push('MarisaServicos_Checkout', { step: nextStep });
    },
    [params.step, push, setAccountDebit, setPage]
  );

  useEffect(() => {
    async function loadBanks() {
      const banksListRespone = await getBanksUseCase();

      setBanksOptions(banksListRespone);
    }

    loadBanks();
  }, []);

  useEffect(() => {
    newStepLog(CheckoutStepNames.ACCOUNT_DEBIT);
  }, [newStepLog]);

  return (
    <CheckoutLayout isValid onSubmit={handleSubmit(onSubmit)}>
      <SafeEnvironment />

      <Input
        control={control}
        label="Nome do titular da conta"
        name="name"
        placeholder="Nome do titular da conta"
        error={errors.name?.message}
        className="mt-6"
        maxLength={100}
        keyboardType="ascii-capable"
      />

      <Input
        control={control}
        label="CPF do titular da conta"
        name="cpf"
        placeholder="Insira os números do CPF sem pontos"
        error={errors.cpf?.message}
        maskConfig={{ type: 'cpf' }}
        keyboardType="number-pad"
      />

      <Select
        control={control}
        label="Banco"
        name="bank"
        placeholder="Selecione o banco"
        error={errors.bank?.message}
        items={banksOptions}
        type="search"
        searchPlaceholder="Digite aqui o nome ou código do seu banco"
      />

      <Input
        control={control}
        label="Nº da Agência"
        name="agency"
        placeholder="Número da Agência sem o dígito"
        error={errors.agency?.message}
        maxLength={4}
        keyboardType="number-pad"
      />

      <View className="flex-row items-center mb-4">
        <Input
          control={control}
          label="Nº da Conta Corrente"
          name="accountNumber"
          placeholder="XXXXX"
          error={errors.accountNumber?.message}
          keyboardType="number-pad"
          className="mr-2 flex-[0.7]"
          maxLength={11}
        />
        <Input
          control={control}
          label="Dígito"
          name="accountDigit"
          placeholder="X"
          error={errors.accountDigit?.message}
          keyboardType="number-pad"
          className="flex-[0.3]"
          maxLength={1}
        />
      </View>

      <Checkbox
        control={control}
        name="acceptNews"
        className={
          errors.accountNumber?.message || errors.accountDigit?.message
            ? ''
            : 'mt-[-16px]'
        }
        error={errors.acceptNews?.message}
      >
        Concordo em receber novidades e dicas de seguro.
      </Checkbox>

      <Checkbox
        control={control}
        name="acceptGeneralConditions"
        error={errors.acceptGeneralConditions?.message}
        className="my-4"
      >
        <View className="flex-row items-center">
          <Text className="text-xs text-gray-600">Li e aceito os </Text>
          <TouchableOpacity activeOpacity={0.6}>
            <Text className="text-primary-600 font-medium text-xs">
              termos e condições gerais
            </Text>
          </TouchableOpacity>
        </View>
      </Checkbox>
    </CheckoutLayout>
  );
};

export default AccountDebit;
